import React, { useMemo, useState } from 'react';
import { dec, familiaDaEspera, num } from './comum';

// Servidor: configuracao como lista de verificacao (estado com icone + palavra,
// nunca so cor) e esperas agrupadas em familias, em barras de uma cor so.

const ESTADO = {
  problema: { icone: 'bi-x-octagon-fill', rotulo: 'Corrigir', cor: 'var(--ab-st-critico)' },
  avaliar: { icone: 'bi-exclamation-triangle-fill', rotulo: 'Avaliar', cor: 'var(--ab-st-aviso)' },
  ok: { icone: 'bi-check-circle-fill', rotulo: 'Ok', cor: 'var(--ab-st-ok)' },
  info: { icone: 'bi-info-circle', rotulo: 'Informativo', cor: 'var(--portal-muted)' },
};

function itensConfig(c) {
  const semManutencao = !/(estat|statist|index|indice|manut|maint|ola)/i.test(c['agent.jobs_ativos'] || '');
  return [
    {
      rotulo: 'Estatísticas automáticas no banco do ERP',
      valor: c['banco.BDENTER.estatistica_automatica'] || '—',
      estado: c['banco.BDENTER.estatistica_automatica'] === 'desligada' ? 'problema' : 'ok',
      nota: 'Estatística velha faz o banco escolher caminhos ruins: é a causa provável dos planos instáveis.',
      quem: 'Microuniverso',
    },
    {
      rotulo: 'Rotina de manutenção de índices e estatísticas',
      valor: semManutencao ? 'nenhuma' : 'existe',
      estado: semManutencao ? 'problema' : 'ok',
      nota: `Jobs ativos no SQL Agent: ${c['agent.jobs_ativos'] || '—'}.`,
      quem: 'Microuniverso / TI',
    },
    {
      rotulo: 'Paralelismo (MAXDOP · limite de custo)',
      valor: `${c['config.max degree of parallelism'] ?? '—'} · ${c['config.cost threshold for parallelism'] ?? '—'}`,
      estado: 'avaliar',
      nota: 'Paralelismo é a maior espera do servidor. Avaliar junto com as consultas que mais leem: corrigir a consulta costuma resolver antes de mexer aqui.',
      quem: 'TI',
    },
    {
      rotulo: 'Query Store no banco do ERP',
      valor: c['banco.BDENTER.query_store'] || '—',
      estado: c['banco.BDENTER.query_store'] === 'ligado' ? 'ok' : 'avaliar',
      nota: 'Sem ele, o histórico das consultas do ERP some quando o servidor reinicia. Ligar depende da sua autorização.',
      quem: 'TI (com autorização)',
    },
    {
      rotulo: 'Optimize for ad hoc workloads',
      valor: c['config.optimize for ad hoc workloads'] === '1' ? 'ligado' : 'desligado',
      estado: 'info',
      nota: 'Com muita consulta montada com o valor no texto, o cache enche de planos usados uma vez só.',
      quem: 'TI',
    },
    {
      rotulo: 'Leitura sem bloqueio (RCSI) no banco do ERP',
      valor: c['banco.BDENTER.rcsi'] || '—',
      estado: 'info',
      nota: 'Desligado, leitura espera gravação terminar (e vice-versa). Mudar exige teste com a Micro.',
      quem: 'Microuniverso',
    },
    {
      rotulo: 'Servidor',
      valor: `SQL Server ${c['servidor.versao'] || ''} · ${c['servidor.cpus'] || '?'} CPUs · ${c['servidor.memoria_gb'] || '?'} GB`,
      estado: 'info',
      nota: `Banco do ERP: ${c['banco.BDENTER.tamanho_gb'] || '?'} GB de dados · BDCENTRAO: ${c['banco.BDCENTRAO.tamanho_gb'] || '?'} GB.`,
      quem: '—',
    },
  ];
}

export default function Servidor({ dados }) {
  const [verCodigos, setVerCodigos] = useState(false);
  const familias = useMemo(() => {
    const porF = new Map();
    (dados.esperas || []).forEach((e) => {
      const f = familiaDaEspera(e.tipo);
      if (!porF.has(f.chave)) porF.set(f.chave, { ...f, ms: 0, codigos: [] });
      const x = porF.get(f.chave);
      x.ms += Number(e.espera_ms);
      x.codigos.push(e);
    });
    return [...porF.values()].sort((a, b) => b.ms - a.ms);
  }, [dados.esperas]);
  const total = familias.reduce((s, f) => s + f.ms, 0) || 1;
  const maior = familias[0]?.ms || 1;

  return (
    <div className="row g-3">
      <div className="col-12 col-xl-6">
        <section className="content-card ab-bloco">
          <div className="ab-bloco__topo">
            <h3>Configuração do servidor</h3>
            <span className="cell-muted">o que está e o que deveria estar</span>
          </div>
          <ul className="ab-check">
            {itensConfig(dados.config || {}).map((i) => {
              const e = ESTADO[i.estado];
              return (
                <li key={i.rotulo}>
                  <span className="ab-check__estado" style={{ color: e.cor }}><i className={`bi ${e.icone}`} /> {e.rotulo}</span>
                  <div>
                    <div className="ab-check__linha"><span className="cell-strong">{i.rotulo}</span><span>{i.valor}</span></div>
                    <small className="cell-muted">{i.nota}{i.quem !== '—' ? ` Quem: ${i.quem}.` : ''}</small>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
      <div className="col-12 col-xl-6">
        <section className="content-card ab-bloco">
          <div className="ab-bloco__topo">
            <h3>Por que as consultas ficam esperando</h3>
            <span className="cell-muted">acumulado desde que o servidor ligou · sem as esperas de fundo</span>
          </div>
          <ul className="ab-hbarras">
            {familias.map((f) => (
              <li key={f.chave} title={f.explica}>
                <div className="ab-hbarras__linha">
                  <span>{f.rotulo}</span>
                  <strong>{dec((100 * f.ms) / total, 1)}%</strong>
                </div>
                <div className="ab-hbarras__trilho"><div style={{ width: `${(100 * f.ms) / maior}%` }} /></div>
                <small className="cell-muted">{f.explica} · {num(f.ms / 3600000)} h</small>
              </li>
            ))}
          </ul>
          <button type="button" className="ab-link" onClick={() => setVerCodigos((v) => !v)}>
            {verCodigos ? 'Esconder os códigos do SQL Server' : 'Ver os códigos do SQL Server'}
          </button>
          {verCodigos && (
            <div className="table-shell" style={{ marginTop: 8 }}>
              <table className="portal-table">
                <thead><tr><th>Código</th><th>Família</th><th style={{ textAlign: 'right' }}>Horas</th><th style={{ textAlign: 'right' }}>%</th></tr></thead>
                <tbody>
                  {(dados.esperas || []).map((e) => (
                    <tr key={e.tipo}>
                      <td><code>{e.tipo}</code></td>
                      <td className="cell-muted">{familiaDaEspera(e.tipo).rotulo}</td>
                      <td style={{ textAlign: 'right' }}>{dec(Number(e.espera_ms) / 3600000, 1)}</td>
                      <td style={{ textAlign: 'right' }}>{dec((100 * Number(e.espera_ms)) / total, 1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
